// 2 つの値の間（段階 C の2 の 7g、2026-10-26。前は d3 v4.10.2 の d3.interpolate*）。
// d3-interpolate（Copyright 2010-2017 Mike Bostock, BSD-3-Clause）を TS に移したもの。
// 動き（transition.ts）が止まったときの属性の文字（座標変換の "translate(x, y) scale(s,s)"・色の "rgb(r, g, b)" など）を前と同じにするため、書き方も同じ
import { Rgb, color, rgb } from './color.js';

export type Interpolator<T = unknown> = (t: number) => T;

const constant =
  <T>(x: T): Interpolator<T> =>
  () =>
    x;

/** 数（d3.interpolateNumber。a + (b - a) * t） */
export function interpolateNumber(a: unknown, b: unknown): Interpolator<number> {
  const a0 = +(a as number), d = +(b as number) - a0;
  return (t) => a0 + d * t;
}

// 色の 1 つの成分（d3 の gamma 1 の nogamma: 差が無ければ同じ値、始めが NaN なら終わりの値のまま）
function nogamma(a: number, b: number): Interpolator<number> {
  const d = b - a;
  return d ? (t) => a + t * d : constant(isNaN(a) ? b : a);
}

/** 色（d3.interpolateRgb。返すのは "rgb(r, g, b)"） */
export function interpolateRgb(start: unknown, end: unknown): Interpolator<string> {
  const s = rgb(start), e = rgb(end);
  const r = nogamma(s.r, e.r), g = nogamma(s.g, e.g), b = nogamma(s.b, e.b), opacity = nogamma(s.opacity, e.opacity);
  return (t) => {
    s.r = r(t);
    s.g = g(t);
    s.b = b(t);
    s.opacity = opacity(t);
    return s + '';
  };
}

const reA = /[-+]?(?:\d+\.?\d*|\.?\d+)(?:[eE][-+]?\d+)?/g,
  reB = new RegExp(reA.source, 'g');

/** 文字の中の数を動かす（d3.interpolateString。"10px" → "20px" など） */
export function interpolateString(a: unknown, b: unknown): Interpolator<string> {
  let bi = (reA.lastIndex = reB.lastIndex = 0),
    am: RegExpExecArray | null,
    bm: RegExpExecArray | null,
    bs: string,
    i = -1;
  const s: (string | null)[] = [], q: { i: number; x: Interpolator<number> }[] = [];
  const sa = a + '', sb = b + '';
  while ((am = reA.exec(sa)) && (bm = reB.exec(sb))) {
    if (bm.index > bi) {
      bs = sb.slice(bi, bm.index);
      if (s[i]) s[i] += bs;
      else s[++i] = bs;
    }
    const an = am[0], bn = bm[0];
    if (an === bn) {
      if (s[i]) s[i] += bn;
      else s[++i] = bn;
    } else {
      s[++i] = null;
      q.push({ i: i, x: interpolateNumber(an, bn) });
    }
    bi = reB.lastIndex;
  }
  if (bi < sb.length) {
    bs = sb.slice(bi);
    if (s[i]) s[i] += bs;
    else s[++i] = bs;
  }
  if (s.length < 2) {
    if (q[0]) {
      const x = q[0].x;
      return (t) => x(t) + '';
    }
    return constant(sb);
  }
  const n = q.length;
  return (t) => {
    for (let k = 0, o; k < n; ++k) s[(o = q[k]).i] = o.x(t) + '';
    return s.join('');
  };
}

// ---- 座標変換（d3.interpolateTransformSvg） ----

interface Decomposed {
  translateX: number;
  translateY: number;
  rotate: number;
  skewX: number;
  scaleX: number;
  scaleY: number;
}
const IDENTITY: Decomposed = { translateX: 0, translateY: 0, rotate: 0, skewX: 0, scaleX: 1, scaleY: 1 };
const DEG = 180 / Math.PI;

function decompose(a: number, b: number, c: number, d: number, e: number, f: number): Decomposed {
  let scaleX, scaleY, skewX;
  if ((scaleX = Math.sqrt(a * a + b * b))) (a /= scaleX), (b /= scaleX);
  if ((skewX = a * c + b * d)) (c -= a * skewX), (d -= b * skewX);
  if ((scaleY = Math.sqrt(c * c + d * d))) (c /= scaleY), (d /= scaleY), (skewX /= scaleY);
  if (a * d < b * c) (a = -a), (b = -b), (skewX = -skewX), (scaleX = -scaleX);
  return { translateX: e, translateY: f, rotate: Math.atan2(b, a) * DEG, skewX: Math.atan(skewX) * DEG, scaleX: scaleX, scaleY: scaleY };
}

let svgNode: SVGGElement | null = null;
/** SVG の transform の文字を分ける（ブラウザに読ませて 1 つの行列にする） */
function parseSvg(value: string | null): Decomposed {
  if (value == null) return IDENTITY;
  if (!svgNode) svgNode = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  svgNode.setAttribute('transform', value);
  const m = svgNode.transform.baseVal.consolidate();
  if (!m) return IDENTITY;
  const x = m.matrix;
  return decompose(x.a, x.b, x.c, x.d, x.e, x.f);
}

/** SVG の transform（d3.interpolateTransformSvg。返すのは "translate(x, y) rotate(r) skewX(k) scale(sx,sy)" の要るものだけ） */
export function interpolateTransformSvg(a: string | null, b: string | null): Interpolator<string> {
  const pop = (s: (string | number | null)[]): string => (s.length ? s.pop() + ' ' : '');
  const s: (string | number | null)[] = [], q: { i: number; x: Interpolator<number> }[] = [];
  const A = parseSvg(a), B = parseSvg(b);
  // translate
  if (A.translateX !== B.translateX || A.translateY !== B.translateY) {
    const i = s.push('translate(', null, ', ', null, ')');
    q.push({ i: i - 4, x: interpolateNumber(A.translateX, B.translateX) }, { i: i - 2, x: interpolateNumber(A.translateY, B.translateY) });
  } else if (B.translateX || B.translateY) {
    s.push('translate(' + B.translateX + ', ' + B.translateY + ')');
  }
  // rotate
  let ra = A.rotate, rb = B.rotate;
  if (ra !== rb) {
    if (ra - rb > 180) rb += 360;
    else if (rb - ra > 180) ra += 360;
    q.push({ i: s.push(pop(s) + 'rotate(', null, ')') - 2, x: interpolateNumber(ra, rb) });
  } else if (rb) {
    s.push(pop(s) + 'rotate(' + rb + ')');
  }
  // skewX
  if (A.skewX !== B.skewX) q.push({ i: s.push(pop(s) + 'skewX(', null, ')') - 2, x: interpolateNumber(A.skewX, B.skewX) });
  else if (B.skewX) s.push(pop(s) + 'skewX(' + B.skewX + ')');
  // scale
  if (A.scaleX !== B.scaleX || A.scaleY !== B.scaleY) {
    const i = s.push(pop(s) + 'scale(', null, ',', null, ')');
    q.push({ i: i - 4, x: interpolateNumber(A.scaleX, B.scaleX) }, { i: i - 2, x: interpolateNumber(A.scaleY, B.scaleY) });
  } else if (B.scaleX !== 1 || B.scaleY !== 1) {
    s.push(pop(s) + 'scale(' + B.scaleX + ',' + B.scaleY + ')');
  }
  return (t) => {
    for (let k = 0, o, n = q.length; k < n; ++k) s[(o = q[k]).i] = o.x(t);
    return s.join('');
  };
}

/** 何でも（d3.interpolate。数・色・文字・配列・物） */
export function interpolate(a: any, b: any): Interpolator {
  const t = typeof b;
  if (b == null || t === 'boolean') return constant(b);
  if (t === 'number') return interpolateNumber(a, b);
  if (t === 'string') {
    const c = color(b);
    return c ? interpolateRgb(a, c) : interpolateString(a, b);
  }
  if (b instanceof Rgb) return interpolateRgb(a, b);
  if (b instanceof Date) {
    const d = new Date(), a0 = +a, db = +b - a0;
    return (x) => (d.setTime(a0 + db * x), d);
  }
  if (Array.isArray(b)) {
    const nb = b.length, na = a ? Math.min(nb, a.length) : 0, x: Interpolator[] = new Array(na), c = new Array(nb);
    let i;
    for (i = 0; i < na; ++i) x[i] = interpolate(a[i], b[i]);
    for (; i < nb; ++i) c[i] = b[i];
    return (u) => {
      for (i = 0; i < na; ++i) c[i] = x[i](u);
      return c;
    };
  }
  if ((typeof b.valueOf !== 'function' && typeof b.toString !== 'function') || isNaN(b)) {
    const i: Record<string, Interpolator> = {}, c: Record<string, unknown> = {};
    const aa = a !== null && typeof a === 'object' ? a : {};
    for (const k in b) {
      if (k in aa) i[k] = interpolate(aa[k], b[k]);
      else c[k] = b[k];
    }
    return (u) => {
      for (const k in i) c[k] = i[k](u);
      return c;
    };
  }
  return interpolateNumber(a, b);
}

/** 動きの属性・CSS の値（d3-transition の interpolate: 数・色・それ以外は文字の中の数） */
export function interpolateForTransition(a: unknown, b: unknown): Interpolator {
  let c: Rgb | null;
  if (typeof b === 'number') return interpolateNumber(a, b);
  if (b instanceof Rgb) return interpolateRgb(a, b);
  if ((c = color(b))) return interpolateRgb(a, c);
  return interpolateString(a, b);
}
